/**
 * PostCutsceneDialogue.tsx — R8 #6
 *
 * Sequential 4-player speech bubble chain shown after every era-transition
 * or ending cutscene. Each player speaks ~3s, one at a time. After all 4
 * have spoken, dismisses itself and triggers GrandTransitionOverlay.
 *
 * Triggered by CutsceneTriggerWatcher when it detects cutsceneGroupId → null
 * AND the completed group was a transition-N group (era-change cutscene).
 *
 * VISIBLE WHEN: pendingPostCutsceneDialogue !== null
 * TEST PATH:
 *   1. Era transitions (round 3→4) → transition-1 cutscene plays.
 *   2. Cutscene ends (skipCutscene or advanceCutsceneFrame runs out).
 *   3. CutsceneTriggerWatcher calls startPostCutsceneDialogue(groupId, era).
 *   4. PostCutsceneDialogue overlay appears: P1 speaks first.
 *   5. After ~3s (or click) → P2, P3, P4 in sequence.
 *   6. After P4: grandTransitionActive fires, overlay dismisses.
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { SEAT_COLORS, useGameStore } from "../../store/game-store.js";
import styles from "./PostCutsceneDialogue.module.css";
import { ARCHETYPE_NAMES, getAllDialogueLines } from "./dialogue-lines.js";

/** Duration each speaker is shown (ms). */
const SPEAKER_DURATION_MS = 3000;

const SEAT_LABELS = ["P1", "P2", "P3", "P4"] as const;

export function PostCutsceneDialogue(): React.ReactElement | null {
  const dialogue = useGameStore((s) => s.pendingPostCutsceneDialogue);
  const advance = useGameStore((s) => s.advancePostCutsceneDialogue);
  const numPlayers = useGameStore((s) => s.numPlayers);

  // Auto-advance after SPEAKER_DURATION_MS.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (dialogue === null) return;

    // Cancel any pending timer from the previous speaker.
    if (timerRef.current !== null) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      advance();
    }, SPEAKER_DURATION_MS);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [dialogue, advance]);

  if (dialogue === null) return null;

  const { era, queue } = dialogue;
  const currentSpeaker = queue[0];
  if (currentSpeaker === undefined) return null;

  const seatIdx = currentSpeaker as 0 | 1 | 2 | 3;
  const seatColor = SEAT_COLORS[seatIdx] ?? "#ffb420";
  const seatLabel = SEAT_LABELS[seatIdx] ?? `P${seatIdx + 1}`;
  const archetype = ARCHETYPE_NAMES[seatIdx] ?? "Player";
  const lines = getAllDialogueLines(seatIdx, era);
  const speakLine = lines[0] ?? "…";

  // Progress dots — one per PRESENT player, filled = (numPlayers - queue.length).
  const totalSpeakers = numPlayers;
  const doneCount = totalSpeakers - queue.length;

  // R9 #8 — Anchor bubble below the speaker's PlayerHUD seat slot.
  // PlayerHUD shows one panel per PRESENT seat, evenly spaced across 100% width;
  // each slot is (100/numPlayers)% wide, centre at (seat × slotW + slotW/2).
  // Bubble appears from top:150px anchored horizontally at that column.
  const SLOT_WIDTH_PCT = 100 / numPlayers;
  const seatLeftPct = seatIdx * SLOT_WIDTH_PCT + SLOT_WIDTH_PCT / 2;

  return (
    <dialog
      open
      className={styles.overlay}
      data-testid="post-cutscene-dialogue"
      data-anchor-seat={seatIdx}
      aria-labelledby="dialogue-text"
      aria-live="polite"
    >
      <div
        className={styles.bubbleWrap}
        style={
          {
            "--anchor-left": `${seatLeftPct}%`,
          } as React.CSSProperties
        }
      >
        {/* Speech bubble — anchored below speaker's seat slot (R9 #8) */}
        <div
          key={`speaker-${seatIdx}-${era}`}
          className={styles.bubble}
          style={
            {
              "--seat-color": seatColor,
              "--seat-glow": `${seatColor}66`,
            } as React.CSSProperties
          }
          data-testid={`dialogue-bubble-seat-${seatIdx}`}
        >
          <div className={styles.speakerRow}>
            <span
              className={styles.speakerPip}
              style={{ backgroundColor: seatColor }}
              aria-hidden="true"
            />
            <span className={styles.speakerLabel} style={{ color: seatColor }}>
              {seatLabel}
            </span>
            <span className={styles.speakerArchetype}>{archetype}</span>
          </div>
          <p id="dialogue-text" className={styles.dialogueText}>
            &ldquo;{speakLine}&rdquo;
          </p>
        </div>

        {/* Progress dots + skip */}
        <div className={styles.progressRow}>
          <div className={styles.dotBar} aria-hidden="true">
            {(["P1", "P2", "P3", "P4"] as const).map((label, i) => (
              <span
                key={label}
                className={[styles.dot, i < doneCount ? styles.dotActive : ""]
                  .filter(Boolean)
                  .join(" ")}
              />
            ))}
          </div>
          <button
            type="button"
            className={styles.skipBtn}
            onClick={advance}
            data-testid="dialogue-advance-btn"
            aria-label="Next speaker"
          >
            NEXT →
          </button>
        </div>
      </div>
    </dialog>
  );
}
